import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useCurrencies } from '../../currencies/api/useCurrencies';
import { usePaymentMethods } from '../../payment-methods/api/usePaymentMethods';
import { useSettings } from '../../settings/api/useSettings';
import { ContactPicker } from '../../../shared/ui/ContactPicker';
import { ErrorMessage } from '../../../shared/ui/ErrorMessage';
import { PageHeader } from '../../../shared/ui/PageHeader';
import { useOnline } from '../../../shared/pwa/useOnline';
import { useCreatePurchase, useLastTradeRate } from '../api/useTrades';
import { decimal, tradeQuote, validAmount } from '../lib/quote';
import { AMOUNT_RE, RATE_RE, normalizeDecimal } from '../../../shared/lib/numericString';

const schema = z.object({
  deliveredCurrencyId: z.string().uuid({ message: 'form.required' }),
  deliveredAmount: z.string().trim().regex(AMOUNT_RE, { message: 'openings.quantity_invalid' }),
  paymentCurrencyId: z.string().uuid({ message: 'form.required' }),
  rate: z.string().trim().regex(RATE_RE, { message: 'openings.avg_cost_invalid' }),
  paymentBaseRate: z
    .string()
    .trim()
    .regex(RATE_RE, { message: 'openings.avg_cost_invalid' })
    .optional()
    .or(z.literal('')),
  immediatePayment: z
    .string()
    .trim()
    .regex(AMOUNT_RE, { message: 'openings.quantity_invalid' })
    .optional()
    .or(z.literal('')),
  paymentMethodId: z.string().uuid().optional().or(z.literal('')),
  paymentMethodNote: z.string().max(500).optional(),
  contactId: z.string().uuid().optional().or(z.literal('')),
  reference: z.string().max(200).optional(),
  notes: z.string().max(2000).optional(),
  transactionDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'openings.date_invalid' }),
});

type FormValues = z.infer<typeof schema>;

export function PurchaseFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const online = useOnline();
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [rateSanityDismissed, setRateSanityDismissed] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const currencies = useCurrencies();
  const methods = usePaymentMethods();
  const settings = useSettings();
  const create = useCreatePurchase();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      deliveredCurrencyId: '',
      deliveredAmount: '',
      paymentCurrencyId: '',
      rate: '',
      paymentBaseRate: '',
      immediatePayment: '',
      paymentMethodId: '',
      paymentMethodNote: '',
      contactId: '',
      reference: '',
      notes: '',
      transactionDate: new Date().toISOString().slice(0, 10),
    },
  });

  const watched = useWatch({ control });
  const deliveredCurrencyId = watched.deliveredCurrencyId;
  const paymentCurrencyId = watched.paymentCurrencyId;
  const deliveredAmount = watched.deliveredAmount ?? '';
  const rate = watched.rate ?? '';
  const paymentBaseRate = watched.paymentBaseRate ?? '';
  const immediatePayment = watched.immediatePayment ?? '';
  const paymentMethodId = watched.paymentMethodId ?? '';

  const deliveredCurrency = currencies.data?.find((c) => c.id === deliveredCurrencyId);
  const paymentCurrency = currencies.data?.find((c) => c.id === paymentCurrencyId);
  const selectedMethod = methods.data?.find((m) => m.id === paymentMethodId);
  const baseCurrencyId = settings.data?.baseCurrencyId;
  const deliveredIsBase = !!baseCurrencyId && deliveredCurrencyId === baseCurrencyId;
  const paymentIsBase = !!baseCurrencyId && paymentCurrencyId === baseCurrencyId;

  useEffect(() => {
    setValue('rate', '');
    setValue('paymentBaseRate', '');
    setValue('immediatePayment', '');
    setRateSanityDismissed(false);
  }, [deliveredCurrencyId, paymentCurrencyId, setValue]);

  const lastRate = useLastTradeRate(
    deliveredCurrencyId || undefined,
    paymentCurrencyId || undefined,
  );

  const quote = tradeQuote({
    amount: deliveredAmount,
    rate,
    paymentBaseRate,
    deliveredIsBase,
    paymentIsBase,
    paymentDp: paymentCurrency?.decimalPlaces ?? 2,
  });
  const derivedTotal = quote?.paymentTotal ?? null;
  const validRate = RATE_RE.test(rate) && decimal(rate).gt(0);
  const showRateWarning =
    !rateSanityDismissed &&
    !!lastRate &&
    validRate &&
    decimal(lastRate).gt(0) &&
    (decimal(rate).gt(decimal(lastRate).times(3)) || decimal(rate).lt(decimal(lastRate).div(3)));
  const immPay = validAmount(immediatePayment) ? decimal(immediatePayment) : decimal('0');
  const remaining = derivedTotal
    ? decimal(derivedTotal)
        .minus(immPay)
        .toFixed(paymentCurrency?.decimalPlaces ?? 2)
    : '0';

  async function onSubmit(values: FormValues) {
    if (!baseCurrencyId || !quote || 'error' in quote) {
      setError('root', { message: quote && 'error' in quote ? quote.error : 'form.required' });
      return;
    }
    if (values.deliveredCurrencyId === values.paymentCurrencyId) {
      setError('paymentCurrencyId', { message: 'trades.different_currencies' });
      return;
    }
    if (immPay.gt(quote.paymentTotal)) {
      setError('immediatePayment', { message: 'trades.overpayment' });
      return;
    }

    const input = {
      deliveredCurrencyId: values.deliveredCurrencyId,
      deliveredAmount: normalizeDecimal(values.deliveredAmount),
      paymentCurrencyId: values.paymentCurrencyId,
      ...quote,
      ...(values.immediatePayment
        ? { immediatePayment: normalizeDecimal(values.immediatePayment) }
        : {}),
      ...(values.paymentMethodId ? { paymentMethodId: values.paymentMethodId } : {}),
      ...(values.paymentMethodNote ? { paymentMethodNote: values.paymentMethodNote } : {}),
      ...(values.contactId ? { contactId: values.contactId } : {}),
      ...(values.reference ? { reference: values.reference } : {}),
      ...(values.notes ? { notes: values.notes } : {}),
      transactionDate: new Date(values.transactionDate).toISOString(),
    };
    const result = await create.mutateAsync({ input, idempotencyKey });
    setIdempotencyKey(crypto.randomUUID());
    navigate(`/purchases/${result.id}`);
  }

  const fromCode = deliveredCurrency?.code ?? '—';
  const toCode = paymentCurrency?.code ?? '—';
  const baseCode = currencies.data?.find((c) => c.id === baseCurrencyId)?.code ?? '—';

  const activeMethods = methods.data?.filter((m) => m.isActive) ?? [];

  return (
    <>
      <PageHeader title={t('purchases.new')} />
      <form className="form trade-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Currency pickers — two compact selects side by side */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
          <div className="field">
            <label htmlFor="pf-delivered-currency">{t('purchases.from_currency')}</label>
            <select id="pf-delivered-currency" {...register('deliveredCurrencyId')}>
              <option value="">{t('common.choose')}</option>
              {currencies.data
                ?.filter((c) => c.isActive && c.id !== paymentCurrencyId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code}
                  </option>
                ))}
            </select>
            {errors.deliveredCurrencyId ? (
              <p className="field__error">{t('form.required')}</p>
            ) : null}
          </div>
          <div className="field">
            <label htmlFor="pf-payment-currency">{t('purchases.to_currency')}</label>
            <select id="pf-payment-currency" {...register('paymentCurrencyId')}>
              <option value="">{t('common.choose')}</option>
              {currencies.data
                ?.filter((c) => c.isActive && c.id !== deliveredCurrencyId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code}
                  </option>
                ))}
            </select>
            {errors.paymentCurrencyId ? <p className="field__error">{t('form.required')}</p> : null}
          </div>
        </div>

        {/* JE REÇOIS — big delivered amount display */}
        <div className="trade-amount-display">
          <label htmlFor="pf-delivered-amount" className="trade-amount-display__label">
            {t('purchases.i_receive')}
          </label>
          <p className="trade-amount-display__value">
            <input
              id="pf-delivered-amount"
              {...register('deliveredAmount')}
              inputMode="decimal"
              placeholder="0.00"
              aria-invalid={!!errors.deliveredAmount}
              style={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontFamily: 'var(--font-mono)',
                fontSize: 'inherit',
                fontWeight: 'inherit',
                letterSpacing: 'inherit',
                color: 'inherit',
                textAlign: 'center',
                inlineSize: '100%',
                padding: 0,
              }}
            />
            {fromCode !== '—' ? (
              <span className="trade-amount-display__currency">{fromCode}</span>
            ) : null}
          </p>
          {errors.deliveredAmount ? (
            <p className="field__error">{t('openings.quantity_invalid')}</p>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="pf-rate">{t('trades.pair_rate', { from: fromCode, to: toCode })}</label>
          <input
            id="pf-rate"
            {...register('rate')}
            inputMode="decimal"
            placeholder="0.00"
            aria-invalid={!!errors.rate}
            dir="ltr"
          />
          {errors.rate ? <p className="field__error">{t('openings.avg_cost_invalid')}</p> : null}
          {validRate ? (
            <p dir="ltr">
              1 {fromCode} = {normalizeDecimal(rate)} {toCode}
            </p>
          ) : null}
        </div>

        {deliveredCurrencyId && paymentCurrencyId && !deliveredIsBase && !paymentIsBase ? (
          <div className="field">
            <label htmlFor="pf-payment-rate">
              {t('trades.reporting_rate', { code: toCode, base: baseCode })}
            </label>
            <input
              id="pf-payment-rate"
              {...register('paymentBaseRate')}
              inputMode="decimal"
              placeholder="0.00"
              dir="ltr"
              aria-describedby="pf-reporting-help"
            />
            <p id="pf-reporting-help" className="field__hint">
              {t('trades.reporting_help', { base: baseCode })}
            </p>
          </div>
        ) : null}
        {quote && 'error' in quote ? (
          <p className="field__error" role="alert">
            {t(quote.error ?? 'form.required')}
          </p>
        ) : null}

        {/* Reversed-rate sanity warning */}
        {showRateWarning && lastRate ? (
          <div className="notice notice--warn" role="alert">
            {t('purchases.rate_warning', {
              from: fromCode,
              last: decimal(lastRate).toFixed(4),
              to: toCode,
            })}
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setRateSanityDismissed(true)}
            >
              {t('purchases.rate_warning_dismiss')}
            </button>
          </div>
        ) : null}

        {/* JE RÈGLE — payment total */}
        {derivedTotal ? (
          <div className="trade-amount-display" style={{ background: 'var(--out-tint)' }}>
            <p className="trade-amount-display__label" style={{ color: 'var(--out-text)' }}>
              {t('purchases.i_pay')}
            </p>
            <p className="trade-amount-display__value" style={{ color: 'var(--out-text-deep)' }}>
              {derivedTotal}
              <span className="trade-amount-display__currency">{toCode}</span>
            </p>
          </div>
        ) : null}

        {/* Payment method tabs */}
        {activeMethods.length > 0 ? (
          <>
            <div className="method-tabs" role="group">
              <button
                type="button"
                className={`method-tabs__item${paymentMethodId === '' ? ' is-active' : ''}`}
                onClick={() => setValue('paymentMethodId', '')}
              >
                —
              </button>
              {activeMethods.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={`method-tabs__item${paymentMethodId === m.id ? ' is-active' : ''}`}
                  onClick={() => setValue('paymentMethodId', m.id)}
                >
                  {m.labelFr}
                </button>
              ))}
            </div>
            {selectedMethod?.requiresNote ? (
              <div className="field">
                <label htmlFor="pf-method-note">{t('purchases.method_note')}</label>
                <textarea id="pf-method-note" {...register('paymentMethodNote')} rows={2} />
              </div>
            ) : null}
          </>
        ) : null}

        {/* Immediate payment */}
        <div className="field">
          <label htmlFor="pf-immediate">
            {t('purchases.immediate_payment')} ({toCode})
          </label>
          <input
            id="pf-immediate"
            {...register('immediatePayment')}
            inputMode="decimal"
            placeholder="0.00"
          />
        </div>

        {/* Summary rows — Payé / Reste / Contact */}
        <div className="summary-row-list">
          <div className="summary-row">
            <span className="summary-row__label">{t('purchases.paid_now')}</span>
            <span className="summary-row__value summary-row__value--in">
              {immPay.gt(0) ? immPay.toFixed(paymentCurrency?.decimalPlaces ?? 2) : '—'}{' '}
              {immPay.gt(0) ? toCode : ''}
            </span>
          </div>
          <div className="summary-row">
            <span className="summary-row__label">{t('purchases.remaining_due')}</span>
            <span
              className={`summary-row__value${decimal(remaining).gt(0) ? ' summary-row__value--out' : ''}`}
            >
              {derivedTotal ? `${remaining} ${toCode}` : '—'}
            </span>
          </div>
          <div className="summary-row">
            <span className="summary-row__label">{t('purchases.contact_optional')}</span>
            <div style={{ flex: 1, minInlineSize: 0 }}>
              <Controller
                name="contactId"
                control={control}
                render={({ field }) => (
                  <ContactPicker
                    id="pf-contact"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    allowNone
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* Collapsible: Référence, notes et date */}
        <div>
          <button
            type="button"
            className="collapsible__toggle"
            aria-expanded={detailsOpen}
            onClick={() => setDetailsOpen((v) => !v)}
          >
            {t('purchases.ref_notes_date')}
            <span className="collapsible__chevron" aria-hidden="true">
              ▾
            </span>
          </button>
          {detailsOpen ? (
            <div className="collapsible__content">
              <div className="field">
                <label htmlFor="pf-ref">{t('purchases.reference')}</label>
                <input id="pf-ref" {...register('reference')} maxLength={200} />
              </div>
              <div className="field">
                <label htmlFor="pf-notes">{t('purchases.notes')}</label>
                <textarea id="pf-notes" {...register('notes')} rows={3} maxLength={2000} />
              </div>
              <div className="field">
                <label htmlFor="pf-date">{t('purchases.date')}</label>
                <input id="pf-date" type="date" {...register('transactionDate')} />
              </div>
            </div>
          ) : null}
        </div>

        {errors.immediatePayment ? (
          <p className="field__error" role="alert">
            {t(errors.immediatePayment.message ?? 'openings.quantity_invalid')}
          </p>
        ) : null}
        {errors.root?.message ? (
          <p className="field__error" role="alert">
            {t(errors.root.message)}
          </p>
        ) : null}
        {currencies.error ? <ErrorMessage error={currencies.error} /> : null}
        {settings.error ? <ErrorMessage error={settings.error} /> : null}
        {create.error ? <ErrorMessage error={create.error} /> : null}

        {/* Sticky submit */}
        <div className="sticky-action-bar">
          <button type="button" className="btn btn--ghost" onClick={() => navigate('/purchases')}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={isSubmitting || !online}
            title={!online ? t('purchases.offline_submit') : undefined}
          >
            {t('purchases.save_btn')}
          </button>
        </div>
      </form>
    </>
  );
}
